import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { ListaAttesaDetails } from "../types/Booking";
import { getUserInfo, ClientPrincipal } from "../authConfig";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const CancelWaitingList: React.FC = () => {
  const { listaAttesaId } = useParams<{ listaAttesaId: string }>();
  const navigate = useNavigate();
  const [listaAttesa, setListaAttesa] = useState<ListaAttesaDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [user, setUser] = useState<ClientPrincipal | null>(null);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      const userInfo = await getUserInfo();
      setUser(userInfo);
    };
    checkAuth();
  }, []);

  const backPath = user ? "/le-mie-prenotazioni" : "/";

  useEffect(() => {
    const fetchListaAttesa = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError("Iscrizione alla lista d'attesa non trovata");
          } else {
            setError("Errore nel recupero dei dati");
          }
          return;
        }
        const data = await response.json();
        setListaAttesa(data);
      } catch (err) {
        setError("Errore di connessione");
      } finally {
        setLoading(false);
      }
    };

    fetchListaAttesa();
  }, [listaAttesaId]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API_BASE_URL}/lista-attesa/${listaAttesaId}/cancel`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        setError(errorText);
        return;
      }
      
      setCancelled(true);
    } catch (err) {
      setError("Errore nella rimozione dalla lista d'attesa");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Modal-like container
  const ModalContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
      <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.900" backdropFilter="blur(8px)" zIndex={100} />
      <Flex position="fixed" top={0} left={0} right={0} bottom={0} zIndex={101} align="center" justify="center" p={4}>
        <Box 
          bg="gray.900" 
          w="full" 
          maxW="xl" 
          maxH="85vh"
          overflowY="auto"
          border="1px solid" 
          borderColor="whiteAlpha.200"
          display="flex"
          flexDirection="column"
        >
          {children}
        </Box>
      </Flex>
    </>
  );

  // Header component
  const Header: React.FC<{ subtitle: string; title?: string }> = ({ subtitle, title }) => (
    <Box pt={8} pb={4} px={6} flexShrink={0}>
      <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="widest" mb={title ? 2 : 0}>
        {subtitle}
      </Text>
      {title && (
        <Text fontSize="xl" fontWeight="400" color="white" textTransform="uppercase" letterSpacing="wide">
          {title}
        </Text>
      )}
    </Box>
  );

  // Footer component
  const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <HStack gap={3} p={6} borderTop="1px solid" borderColor="whiteAlpha.200" flexShrink={0} justify="center">
      {children}
    </HStack>
  );

  if (loading) {
    return (
      <ModalContainer>
        <VStack py={20} spacing={4}>
          <Spinner size="xl" color="white" thickness="3px" />
          <Text color="gray.400" textTransform="uppercase" letterSpacing="wide" fontSize="sm">
            Caricamento...
          </Text>
        </VStack>
      </ModalContainer>
    );
  }

  if (error) {
    return (
      <ModalContainer>
        <Header subtitle="ERRORE" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="red.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="red.400">✕</Text>
          </Box>
          <Text color="gray.400" textAlign="center">{error}</Text>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to={backPath}
            bg="transparent" 
            color="white" 
            border="1px solid" 
            borderColor="whiteAlpha.300" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "white", color: "gray.900" }}
          >
            INDIETRO
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  if (cancelled) {
    return (
      <ModalContainer>
        <Header subtitle="CONFERMA" />
        <VStack spacing={6} p={6} flex={1}>
          <Box
            w="80px"
            h="80px"
            borderRadius="full"
            border="2px solid"
            borderColor="green.400"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="3xl" color="green.400">✓</Text>
          </Box>
          <Text
            fontSize="xl"
            fontWeight="500"
            color="white"
            textTransform="uppercase"
            letterSpacing="widest"
            textAlign="center"
          >
            RIMOSSO DALLA LISTA
          </Text>
          <Text
            fontSize="xs"
            color="gray.400"
            textTransform="uppercase"
            letterSpacing="wide"
            textAlign="center"
          >
            Sei stato rimosso dalla lista d'attesa con successo
          </Text>
        </VStack>
        <Footer>
          <Button 
            as={RouterLink}
            to={backPath}
            bg="white" 
            color="gray.900" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "gray.200" }}
          >
            INDIETRO
          </Button>
        </Footer>
      </ModalContainer>
    );
  }

  if (!listaAttesa) {
    return null;
  }

  const eventDate = new Date(listaAttesa.DataEvento);
  const now = new Date();
  const isPast = eventDate < now;

  return (
    <ModalContainer>
      <Header subtitle="LISTA D'ATTESA" title={listaAttesa.EventoTitolo} />
      <VStack spacing={6} p={6} flex={1} align="stretch">
        <Box
          w="80px"
          h="80px"
          borderRadius="full"
          border="2px solid"
          borderColor="orange.400"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
        >
          <Text fontSize="3xl" color="orange.400">?</Text>
        </Box>
        <Text
          fontSize="lg"
          fontWeight="500"
          color="white"
          textTransform="uppercase"
          letterSpacing="widest"
          textAlign="center"
        >
          CONFERMA RIMOZIONE
        </Text>
        <Text
          fontSize="xs"
          color="gray.400"
          textTransform="uppercase"
          letterSpacing="wide"
          textAlign="center"
        >
          Sei sicuro di volerti rimuovere dalla lista d'attesa?
        </Text>

        <Box w="full" h="1px" bg="whiteAlpha.200" />

        <Box bg="whiteAlpha.50" p={5} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontSize="lg" fontWeight="light" letterSpacing="widest" textTransform="uppercase" color="white">{listaAttesa.EventoTitolo}</Text>
          <Text color="gray.400" fontSize="xs" mt={2} textTransform="uppercase" letterSpacing="wide">
            {formatDate(listaAttesa.DataEvento)}
          </Text>
        </Box>

        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">INTESTATARIO</Text>
              <Text fontWeight="medium" color="white" textTransform="uppercase" letterSpacing="wide">{listaAttesa.Nome} {listaAttesa.Cognome}</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">PARTECIPANTI</Text>
              <Text color="white">{listaAttesa.NumeroPersone}</Text>
            </HStack>
            <Box w="full" h="1px" bg="whiteAlpha.200" my={1} />
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">POSIZIONE IN LISTA</Text>
              <Text fontWeight="medium" color="white" fontSize="lg">#{listaAttesa.Priorita}</Text>
            </HStack>
          </VStack>
        </Box>

        {isPast && (
          <Alert status="warning" borderRadius="none" bg="whiteAlpha.100" border="1px solid" borderColor="orange.500">
            <AlertIcon color="orange.400" />
            <Text fontSize="xs" color="white" textTransform="uppercase" letterSpacing="wide">
              Non è possibile rimuoversi dalla lista d'attesa per un evento già passato
            </Text>
          </Alert>
        )}
      </VStack>
      <Footer>
        <Button 
          onClick={() => navigate(backPath)}
          bg="transparent" 
          color="gray.400" 
          borderRadius="none"
          textTransform="uppercase"
          letterSpacing="widest"
          fontSize="sm"
          _hover={{ color: "white" }}
        >
          ANNULLA
        </Button>
        {!isPast && (
          <Button 
            onClick={handleCancel}
            isLoading={cancelling}
            loadingText="RIMOZIONE..."
            bg="red.500" 
            color="white" 
            borderRadius="none"
            textTransform="uppercase"
            letterSpacing="widest"
            fontSize="sm"
            _hover={{ bg: "red.600" }}
          >
            CONFERMA RIMOZIONE
          </Button>
        )}
      </Footer>
    </ModalContainer>
  );
};

export default CancelWaitingList;
